import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";

const inputCls = "px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

const fmtDate = (s) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "");

function JustifFilters({ classes, students, classId, setClassId, studentId, setStudentId, data }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="text-sm">
        <span className="eyebrow block mb-1">Classe</span>
        <select className={inputCls} value={classId} onChange={(e) => setClassId(e.target.value)} data-testid="justifs-class-select">
          <option value="">Toutes</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>
      <label className="text-sm">
        <span className="eyebrow block mb-1">Élève</span>
        <select className={inputCls} value={studentId} onChange={(e) => setStudentId(e.target.value)} disabled={!classId} data-testid="justifs-student-select">
          <option value="">{classId ? "Tous" : "Choisir une classe"}</option>
          {students.map((s) => <option key={s.id} value={s.id}>{s.last_name.toUpperCase()} {s.first_name}</option>)}
        </select>
      </label>
      {data && (
        <div className="text-sm pb-2" data-testid="justifs-total">
          <span className="font-mono-spec font-semibold text-[var(--ok)]">{data.total}</span>
          <span className="text-[var(--ink-soft)]"> justification{data.total > 1 ? "s" : ""}</span>
        </div>
      )}
    </div>
  );
}

function JustificationsContent({ loading, data }) {
  if (loading || !data) {
    return <div className="mt-4 text-[var(--ink-soft)]">Chargement...</div>;
  }
  if (data.rows.length === 0) {
    return <div className="mt-4 text-sm text-[var(--ink-soft)] italic">Aucune absence justifiée sur cette sélection.</div>;
  }
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm" data-testid="justifications-table">
        <thead>
          <tr className="border-b border-[var(--parchment-2)] text-left">
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Date</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Créneau</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Élève</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Classe</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Motif</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Justifiée par</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={`${r.student.id}-${r.date}-${r.slot}`} className="border-b border-[var(--parchment-2)]/60" data-testid={`justif-row-${i}`}>
              <td className="py-3 font-mono-spec text-xs">{fmtDate(r.date)}</td>
              <td className="py-3 font-mono-spec text-xs">{r.slot_label}</td>
              <td className="py-3 font-medium">{r.student.last_name.toUpperCase()} {r.student.first_name}</td>
              <td className="py-3 text-[var(--ink-soft)]">{r.class?.name || "—"}</td>
              <td className="py-3">
                <span className="inline-flex items-center gap-1.5 text-[var(--ok)]">
                  <BadgeCheck size={14} /> {r.justification}
                </span>
              </td>
              <td className="py-3 text-xs text-[var(--ink-soft)]">{r.justified_by_name || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useJustifications() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [classId, setClassId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/classes").then(({ data }) => setClasses(data));
  }, []);

  useEffect(() => {
    setStudentId("");
    if (!classId) {
      setStudents([]);
      return;
    }
    api.get(`/classes/${classId}/students`).then(({ data }) => setStudents(data));
  }, [classId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (classId) params.class_id = classId;
      if (studentId) params.student_id = studentId;
      const { data } = await api.get("/admin/justifications", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [classId, studentId]);

  useEffect(() => {
    load();
  }, [load]);

  return { classes, students, classId, setClassId, studentId, setStudentId, data, loading };
}

export default function JustificationsView() {
  const { classes, students, classId, setClassId, studentId, setStudentId, data, loading } = useJustifications();

  return (
    <section className="bg-white border border-[var(--parchment-2)] p-6" data-testid="justifications-view">
      <div className="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-[var(--parchment-2)]">
        <div>
          <div className="eyebrow">Historique · Année 2026-2027</div>
          <h2 className="font-serif-display text-2xl font-semibold mt-1">Absences justifiées</h2>
        </div>
        <JustifFilters
          classes={classes}
          students={students}
          classId={classId}
          setClassId={setClassId}
          studentId={studentId}
          setStudentId={setStudentId}
          data={data}
        />
      </div>

      <JustificationsContent loading={loading} data={data} />
    </section>
  );
}
